import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { FormProvider, } from 'react-hook-form';
import { styled } from '@mui/material';
import { FormContext } from './context';
import useForm from './hooks/useForm';
import { getStyle, formClasses } from './style';
import { HOOK_MARK, defaultValidateMessages } from './constants';
import { useLatest } from '../../hooks/useLatest';
const FormBase = (props, ref) => {
    const { form, validateTrigger = 'onChange', initialValues, onFinish = () => { }, onFinishFailed, children, onValuesChange, scrollToFirstError, labelAlign, labelCol, labelWrap, wrapperCol, layout = 'horizontal', colon, className, disabled, validateMessages = defaultValidateMessages, name: formName, } = props;
    const onValuesChangeLatest = useLatest(onValuesChange);
    const reValidateMode = validateTrigger === 'all' || validateTrigger === 'onTouched'
        ? 'onChange'
        : validateTrigger;
    const [wrapForm] = useForm({
        mode: validateTrigger,
        reValidateMode,
        criteriaMode: 'all',
        initialValues,
    });
    const mergedForm = form ?? wrapForm;
    mergedForm.__INTERNAL__.name = formName;
    const mountRef = React.useRef(null);
    if (!mountRef.current) {
        mountRef.current = true;
    }
    React.useImperativeHandle(ref, () => mergedForm);
    const { handleSubmit, setCallbacks, rhfForm, watch } = mergedForm._getInternalHooks(HOOK_MARK);
    const onInternalFinishFailed = useCallback(() => {
        onFinishFailed?.(mergedForm.getFieldsError());
        if (scrollToFirstError) {
            setTimeout(() => {
                const errorDOM = document.querySelectorAll(`.${formClasses.item.hasError}`)?.[0];
                errorDOM?.scrollIntoView?.(scrollToFirstError);
            }, 0);
        }
    }, [onFinishFailed, scrollToFirstError, mergedForm]);
    const handleSubmitFailed = useCallback(() => {
        onInternalFinishFailed();
    }, [onInternalFinishFailed]);
    const onInternalFinish = useCallback(async (data) => {
        await form?.validateFields();
        onFinish(data);
    }, [form, onFinish]);
    const wrapHandleSubmit = useMemo(() => handleSubmit(onInternalFinish, handleSubmitFailed), [handleSubmit, onInternalFinish, handleSubmitFailed]);
    const formContextValue = useMemo(() => ({
        form: mergedForm,
        name: formName,
        labelAlign,
        labelCol,
        labelWrap,
        wrapperCol,
        layout,
        colon,
        initialValues,
        disabled,
        validateMessages,
    }), [
        formName,
        mergedForm,
        labelAlign,
        labelCol,
        labelWrap,
        wrapperCol,
        layout,
        colon,
        initialValues,
        disabled,
        validateMessages,
    ]);
    useEffect(() => {
        const subscription = watch((values, { type, name }) => {
            onValuesChangeLatest.current?.(values, { type, name });
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [onValuesChangeLatest, watch]);
    setCallbacks({
        onFinish,
        onFinishFailed: onInternalFinishFailed,
    });
    const cls = clsx(className, formClasses.root, {
        [`${formClasses.layout.vertical}`]: layout === 'vertical',
        [`${formClasses.layout.inline}`]: layout === 'inline',
        [`${formClasses.layout.horizontal}`]: layout === 'horizontal',
    });
    return (React.createElement(FormContext.Provider, { value: formContextValue },
        React.createElement(FormProvider, { ...rhfForm },
            React.createElement("form", { onSubmit: wrapHandleSubmit, className: cls }, children))));
};
const StyledForm = styled(React.forwardRef(FormBase))((props) => {
    const { theme } = props;
    return getStyle(theme);
});
export const Form = React.forwardRef((props, ref) => (React.createElement(StyledForm, { ...props, ref: ref })));
